import * as React from 'react';
import { Box, ParagraphText } from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';

/**
 * Props for the MoveItemsList component
 * @interface MoveItemsListProps
 * @property {string[]} selectedItems - Array of selected items
 */
interface MoveItemsListProps {
  selectedItems: string[];
}

/**
 * A list component that displays selected items.
 * Handles the display of a description and a list of selected items.
 *
 * @component
 * @example
 * ```tsx
 * <MoveItemsList
 *   selectedItems={["Item 1", "Item 2"]}
 * />
 * ```
 */
export const MoveItemsList: React.FC<MoveItemsListProps> = ({
  selectedItems,
}) => {
  const { formatMessage } = useIntl();

  return (
    <Box>
      <ParagraphText css={{ fontWeight: '$bold', fontSize: '$bodyTwo' }}>
        {formatMessage(
          {
            id: 'MOVE_ITEMS_DESCRIPTION',
          },
          {
            count: selectedItems.length,
          },
        )}
      </ParagraphText>
      <Box css={{ marginBlockStart: '$threeQuarters' }}>
        <Box
          as="ul"
          css={{
            listStyle: 'none',
            marginBlock: 0,
            marginInline: 0,
            paddingBlock: 0,
            paddingInline: 0,
          }}
        >
          {selectedItems.map((item) => (
            <Box as="li" key={item}>
              <ParagraphText
                css={{
                  fontSize: '$bodyTwo',
                  marginInlineStart: '$four',
                  fontWeight: '$bold',
                }}
              >
                {item}
              </ParagraphText>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};
